import React, { useState, memo } from "react";
import ReactDOM from "react-dom";
import "./MatchRow.css";
import { MatchModel } from "../../models";
import ReactCountryFlag from "react-country-flag";
import { useTranslation } from "react-i18next";
import i18n from '../../i18n';
import { matchInstantUtc, formatMatchTimeLocal } from "../../utils/dateUtils";
import {
  FaFutbol,
  FaBasketballBall,
  FaVolleyballBall,
  FaFootballBall,
  FaBaseballBall,
  FaHockeyPuck,
  FaMale,
  FaFemale,
  FaLock
} from "react-icons/fa";
import { MdSportsHandball } from "react-icons/md";
import { BsPeopleFill } from "react-icons/bs";
import { MatchGenderEnum } from "../../models/enums/MatchGenderEnum";
import { MatchStatusEnum } from "../../models/enums/MatchStatusEnum";
import { Link } from "react-router-dom";
import { generateMatchSlug } from "../../utils/slugGenerator";

interface MatchRowProps {
  match: MatchModel;
  // Stable (useCallback-wrapped) callback from App.tsx via MatchList.
  // MatchRow calls onSelectMatch(match.id) itself — no per-row lambda needed in MatchList.
  onSelectMatch: (matchId: number) => void;
  isSelected: boolean;
}

// Portal-based Tooltip Component
interface TooltipProps {
  text: string;
  children: React.ReactNode;
}

// Tooltip is memoized — it has its own hover state and creates a portal.
// Without memo it re-mounts on every MatchRow render even when not hovered.
const Tooltip: React.FC<TooltipProps> = memo(({ text, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPosition({ x: rect.left + rect.width / 2, y: rect.top - 8 });
    setIsVisible(true);
  };

  const handleMouseLeave = () => setIsVisible(false);

  return (
    <>
      <span className="tooltip-trigger" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </span>
      {isVisible && text && ReactDOM.createPortal(
        <div className="portal-tooltip" style={{ left: position.x, top: position.y }}>
          {text}
        </div>,
        document.body
      )}
    </>
  );
});

// Returns a heat class name based on today's entry count — drives the badge color
const getHeatClass = (count: number): string => {
  if (count === 0)   return "heat-0";
  if (count < 3)     return "heat-1";
  if (count < 8)     return "heat-2";
  if (count < 20)    return "heat-3";
  if (count < 50)    return "heat-4";
  return "heat-5";
};

// Format entry count with Turkish-style abbreviations (e.g. 1600 -> "1,6b", 1200000 -> "1,2m")
const formatEntryCount = (count: number): string => {
  if (count === 0) return "0";
  if (count >= 1_000_000) return (count / 1_000_000).toFixed(1).replace(".", ",") + "m";
  if (count >= 1_000)     return (count / 1_000).toFixed(1).replace(".", ",") + "b";
  return String(count);
};

// MatchRow is memoized so the 300-row list does not re-render when unrelated
// parent state changes (polling badge, UserStatsBar fetch, unread count, etc.).
// Re-renders only when match data, isSelected, or onSelectMatch reference changes.
// onSelectMatch must be stable (useCallback in App.tsx) for memo to be effective.
const MatchRowInner: React.FC<MatchRowProps> = ({ match, onSelectMatch, isSelected }) => {
  const { t } = useTranslation();

  // Show dashes for SCHEDULED matches — status is the source of truth, not the score.
  // A 0-0 completed match must show "0 - 0", not dashes.
  const isScheduled = match.status === MatchStatusEnum.SCHEDULED;

  // LIVE: match is currently in progress — show pulsing red dot next to the time
  const isLive = match.status === MatchStatusEnum.LIVE;

  // POSTPONED / CANCELLED matches never carry a meaningful score: the stored
  // 0-0 is just the default, so rendering it made an postponed match look like
  // a goalless draw. Show a short status badge instead of the score boxes.
  const isPostponed = match.status === MatchStatusEnum.POSTPONED;
  const isCancelled = match.status === MatchStatusEnum.CANCELLED;
  const hasNoScore  = isScheduled || isPostponed || isCancelled;

  // Short status code shown in place of the score ("ERT" / "İPT")
  const statusShort = isPostponed
    ? t("match_status_short_postponed")
    : isCancelled
    ? t("match_status_short_cancelled")
    : null;

  // Format day, abbreviated month and time for the date column.
  // matchDate + matchTime together form one UTC instant; render it in the viewer's local timezone.
  const matchInstant = matchInstantUtc(match.matchDate, match.matchTime);
  const day   = matchInstant ? matchInstant.toLocaleDateString(i18n.language, { day: "2-digit" }) : "";
  const month = matchInstant ? matchInstant.toLocaleDateString(i18n.language, { month: "short" }) : "";
  const time  = matchInstant ? formatMatchTimeLocal(match.matchDate, match.matchTime, i18n.language) : "";

  const getSportIcon = () => {
    const p = { size: 10, style: { opacity: 0.6 } };
    switch (match.sportType) {
      case "FOOTBALL":          return <FaFutbol {...p} />;
      case "BASKETBALL":        return <FaBasketballBall {...p} />;
      case "VOLLEYBALL":        return <FaVolleyballBall {...p} />;
      case "AMERICAN_FOOTBALL": return <FaFootballBall {...p} />;
      case "BASEBALL":          return <FaBaseballBall {...p} />;
      case "ICE_HOCKEY":        return <FaHockeyPuck {...p} />;
      case "HANDBALL":          return <MdSportsHandball {...p} />;
      default:                  return <FaFutbol {...p} />;
    }
  };

  const getGenderIcon = () => {
    const p = { size: 9, style: { opacity: 0.6 } };
    switch (match.matchGender) {
      case MatchGenderEnum.FEMALE: return <FaFemale {...p} className="gender-icon female" />;
      case MatchGenderEnum.MIXED:  return <BsPeopleFill {...p} className="gender-icon mixed" />;
      default:                     return <FaMale {...p} className="gender-icon male" />;
    }
  };

  // Helper function to get translated sport type name for tooltip
  const getTranslatedSportType = (sportType: string) => {
    switch (sportType) {
      case "FOOTBALL":          return t("menu_sport_football");
      case "BASKETBALL":        return t("menu_sport_basketball");
      case "VOLLEYBALL":        return t("menu_sport_volleyball");
      case "AMERICAN_FOOTBALL": return t("menu_sport_american_football");
      case "BASEBALL":          return t("menu_sport_baseball");
      case "ICE_HOCKEY":        return t("menu_sport_ice_hockey");
      case "HANDBALL":          return t("menu_sport_handball");
      default:                  return sportType;
    }
  };

  // Helper function to get translated gender name for tooltip
  const getTranslatedGender = (matchGender: MatchGenderEnum) => {
    switch (matchGender) {
      case MatchGenderEnum.FEMALE: return t("match_gender_female");
      case MatchGenderEnum.MIXED:  return t("match_gender_mixed");
      default:                     return t("match_gender_male");
    }
  };

  const matchUrl = `/${generateMatchSlug(match)}`;

  return (
    <Link
      to={matchUrl}
      className={`match-row ${isSelected ? "selected" : ""} ${isScheduled ? "scheduled" : "finished"} ${isLive ? "live" : ""} ${isPostponed ? "postponed" : ""} ${isCancelled ? "cancelled" : ""} ${match.isLocked ? "locked" : ""}`}
      onMouseDown={(e) => {
        // Only intercept plain left-clicks for SPA navigation.
        // Middle-click (button=1) and right-click (button=2) are left to the browser
        // so "Open in new tab" and context menu work natively.
        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          onSelectMatch(match.id);
        }
      }}
    >
      {/* Left: date column — time is always shown as-is; live dot is handled separately */}
      <div className="match-row__date-col">
        <span className="match-row__day">{day}</span>
        <span className="match-row__month">{month}</span>
        <span className="match-row__time">{time}</span>
      </div>

      {/* Vertical divider */}
      <div className="match-row__divider" />

      {/* Center: two team rows (names only, no scores) */}
      <div className="match-row__teams">
        <div className="match-row__team-line">
          <Tooltip text={match.homeTeam}>
            <span className="match-row__flag">
              {match.homeTeamLogoUrl
                ? <img src={`${process.env.REACT_APP_API_URL || "http://localhost:8080"}${match.homeTeamLogoUrl}`} alt={match.homeTeam} style={{ width: "1.4em", height: "1.4em", objectFit: "contain" }} />
                : <ReactCountryFlag countryCode={match.homeTeamCountry} svg style={{ width: "1.1em", height: "1.1em" }} />
              }
            </span>
          </Tooltip>
          <Tooltip text={match.homeTeam}>
            <span className="match-row__team-name">{match.homeTeam}</span>
          </Tooltip>
        </div>

        <div className="match-row__team-line">
          <Tooltip text={match.awayTeam}>
            <span className="match-row__flag">
              {match.awayTeamLogoUrl
                ? <img src={`${process.env.REACT_APP_API_URL || "http://localhost:8080"}${match.awayTeamLogoUrl}`} alt={match.awayTeam} style={{ width: "1.4em", height: "1.4em", objectFit: "contain" }} />
                : <ReactCountryFlag countryCode={match.awayTeamCountry} svg style={{ width: "1.1em", height: "1.1em" }} />
              }
            </span>
          </Tooltip>
          <Tooltip text={match.awayTeam}>
            <span className="match-row__team-name">{match.awayTeam}</span>
          </Tooltip>
        </div>
      </div>

      {/* Score column: live dot (if LIVE) centered between the two score boxes */}
      <div className="match-row__score-col">
        {isLive && <span className="match-row__live-dot" title="Maç devam ediyor" />}
        {/* Show PEN badge when penalty shootout was played, UZ when only ET — sits LEFT of scores.
            Never shown for matches without a score (scheduled / postponed / cancelled). */}
        {!hasNoScore && match.homeScorePen != null && (
          <span className="match-row__extra-badge match-row__extra-badge--pen">PEN</span>
        )}
        {!hasNoScore && match.homeScorePen == null && match.homeScoreEt != null && (
          <span className="match-row__extra-badge match-row__extra-badge--et">UZ</span>
        )}
        {statusShort ? (
          // POSTPONED / CANCELLED: a single status badge replaces the score boxes
          <Tooltip text={isPostponed ? t("match_status_postponed") : t("match_status_cancelled")}>
            <span
              className={`match-row__status-badge ${
                isPostponed ? "match-row__status-badge--postponed" : "match-row__status-badge--cancelled"
              }`}
            >
              {statusShort}
            </span>
          </Tooltip>
        ) : (
          <div className="match-row__score-boxes">
            {/* Always show score box: dash for scheduled, total score (normal+ET+PEN) for finished */}
            <span className={`match-row__pts ${isScheduled ? "scheduled" : ""}`}>
              {isScheduled ? "-" : match.homeScore + (match.homeScoreEt ?? 0) + (match.homeScorePen ?? 0)}
            </span>
            <span className={`match-row__pts ${isScheduled ? "scheduled" : ""}`}>
              {isScheduled ? "-" : match.awayScore + (match.awayScoreEt ?? 0) + (match.awayScorePen ?? 0)}
            </span>
          </div>
        )}
      </div>

      {/* Right: sport icon (top) + gender icon (bottom) — vertical layout */}
      <div className="match-row__meta">
        <Tooltip text={getTranslatedSportType(match.sportType)}>
          <span className="sport-icon">{getSportIcon()}</span>
        </Tooltip>
        <Tooltip text={getTranslatedGender(match.matchGender)}>
          <span className="gender-icon-wrapper">{getGenderIcon()}</span>
        </Tooltip>
      </div>

      {/* Far right: entry count badge + lock icon (always rightmost) */}
      <div className="match-row__entry-col">
        <Tooltip text={`${match.recentEntryCount ?? 0} bugünkü entry · ${match.entryCount ?? 0} toplam entry`}>
          <span className={`match-row__entry-badge ${getHeatClass(match.recentEntryCount ?? 0)}`}>
            {formatEntryCount(match.recentEntryCount ?? 0)}
          </span>
        </Tooltip>
        {match.isLocked && (
          <Tooltip text={t("topic_locked_tooltip")}>
            <span className="row-lock-icon"><FaLock size={10} /></span>
          </Tooltip>
        )}
      </div>
    </Link>
  );
};

export const MatchRow = memo(MatchRowInner);